import { uniq, isEqual } from 'lodash';
import { getTextDirection } from 'ricos-content';
import { fromTiptapNode } from 'ricos-converters';
import { Node_Type } from 'ricos-schema';
import { Decoration_Type, Mark_Type } from 'ricos-types';
import { TiptapContentResolver } from '../ContentResolver.js';
import { RESOLVERS_IDS } from './resolvers-ids.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const alwaysVisibleResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.ALWAYS_VISIBLE,
  () => {
    return true;
  }
);
const alwaysActiveResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.ALWAYS_ACTIVE,
  () => {
    return true;
  }
);
const isTextInSelection = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_IN_SELECTION,
  (content = []) => {
    return content.some((node) => node.type.name === "text");
  }
);
const isTextContainsBoldResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_BOLD,
  (content, { getEditorQuery }) => {
    const editorQuery = getEditorQuery();
    const decorations = editorQuery.query.activeDecorationsByType(Decoration_Type.BOLD);
    return decorations.length > 0 && decorations.every((item) => (item == null ? void 0 : item.fontWeightValue) === 700);
  }
);
const isTextContainsItalicResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_ITALIC,
  (content, { styles, nodeService }, editor) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      let hasItalicDecorationInDocumentStyles = false;
      if (node && styles && nodeService) {
        hasItalicDecorationInDocumentStyles = !!content.find((node2) => {
          const decoration = styles.getDecoration(
            nodeService.nodeToRicosNode(node2),
            Decoration_Type.ITALIC
          );
          return decoration.italicData;
        });
      }
      const hasInlineItalic = node == null ? void 0 : node.marks.some((mark) => {
        return mark.type.name === Decoration_Type.ITALIC && mark.attrs.italicData !== false;
      });
      const hasInlineUnsetItalic = node == null ? void 0 : node.marks.some((mark) => {
        return mark.type.name === Decoration_Type.ITALIC && mark.attrs.italicData === false;
      });
      if (hasInlineUnsetItalic) {
        return false;
      }
      if (editor == null ? void 0 : editor.isActive(Decoration_Type.ITALIC, { italicData: true })) {
        return true;
      }
      return hasInlineItalic || hasItalicDecorationInDocumentStyles;
    }
    return false;
  }
);
const isTextContainsUnderlineResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_UNDERLINE,
  (content, { getEditorQuery }, _editor) => {
    const editorQuery = getEditorQuery();
    const decorations = editorQuery.query.activeDecorationsByType(Decoration_Type.UNDERLINE);
    return decorations.length > 0 && decorations.every((item) => (item == null ? void 0 : item.type) === Decoration_Type.UNDERLINE);
  }
);
const isTextContainsQuoteResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_QUOTE,
  (content) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => node2.type.name === Node_Type.BLOCKQUOTE);
      return Boolean(node);
    }
    return false;
  }
);
const isTextContainsCodeblockResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_CODE_BLOCK,
  (content) => {
    return Array.isArray(content) && content.length > 0 && content[0].type.name === Node_Type.CODE_BLOCK;
  }
);
const isTextContainsOrderedListResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_ORDERED_LIST,
  (content) => {
    return Array.isArray(content) && content.length > 0 && content[0].type.name === Node_Type.ORDERED_LIST;
  }
);
const isTextContainsUnorderedListResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_UNORDERED_LIST,
  (content) => {
    return Array.isArray(content) && content.length > 0 && content[0].type.name === Node_Type.BULLETED_LIST;
  }
);
const isTextContainsSpoilerResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_SPOILER,
  (content, { getEditorQuery }, _editor) => {
    const editorQuery = getEditorQuery();
    const decorations = editorQuery.query.activeDecorationsByType(Decoration_Type.SPOILER);
    return decorations.length > 0 && decorations.every((item) => (item == null ? void 0 : item.type) === Decoration_Type.SPOILER);
  }
);
const getTextAlignment = (activeTextAlignment, editor) => {
  var _a;
  const textAlignment = activeTextAlignment.toLowerCase();
  if (textAlignment !== "auto")
    return textAlignment;
  const {
    state: {
      doc,
      selection: { from }
    }
  } = editor;
  const nodeToCheck = doc.nodeAt(from);
  const isTextNode = (nodeToCheck == null ? void 0 : nodeToCheck.type.name) === "text";
  const isEmptyLine = (nodeToCheck == null ? void 0 : nodeToCheck.type.name) === void 0;
  if (!(isTextNode || isEmptyLine))
    return;
  const selectedFirstNodeText = (_a = doc.nodeAt(from - 1)) == null ? void 0 : _a.textContent;
  const selectedFirstNodeDirection = getTextDirection(selectedFirstNodeText);
  return selectedFirstNodeDirection === "rtl" ? "right" : "left";
};
const getAlignmentInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_ALIGNMENT_IN_SELECTION,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles("textAlignment");
    if (editor && activeTextStyles.length > 0 && uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor);
    }
    return void 0;
  }
);
const isTextAlignLeft = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_LEFT,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles("textAlignment");
    if (editor && activeTextStyles.length > 0 && uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === "left";
    }
    return void 0;
  }
);
const isTextAlignCenter = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_CENTER,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles("textAlignment");
    if (editor && activeTextStyles.length > 0 && uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === "center";
    }
    return void 0;
  }
);
const isTextAlignRight = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_RIGHT,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles("textAlignment");
    if (editor && activeTextStyles.length > 0 && uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === "right";
    }
    return void 0;
  }
);
const isTextAlignJustify = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_ALIGN_JUSTIFY,
  (_content, { getEditorQuery }, editor) => {
    const editorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles("textAlignment");
    if (editor && activeTextStyles.length > 0 && uniq(activeTextStyles).length === 1) {
      return getTextAlignment(activeTextStyles[0], editor) === "justify";
    }
    return void 0;
  }
);
const getHeadingInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_HEADING_IN_SELECTION,
  (content) => {
    var _a, _b;
    if (Array.isArray(content) && content.length > 0) {
      const headingLevel = (_b = (_a = content.find((node) => node.type.name === Node_Type.HEADING)) == null ? void 0 : _a.attrs) == null ? void 0 : _b.level;
      if (!headingLevel || content[0].type.name === Node_Type.PARAGRAPH)
        return "unstyled";
      const headingMap = {
        1: "header-one",
        2: "header-two",
        3: "header-three",
        4: "header-four",
        5: "header-five",
        6: "header-six"
      };
      return headingMap[headingLevel];
    } else {
      return void 0;
    }
  }
);
const isHeaderTwoOrThreeSelected = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_HEADER_TWO_OR_THREE_SELECTED,
  (content) => {
    var _a;
    if (Array.isArray(content) && content.length > 0 && content[0].type.name !== Node_Type.PARAGRAPH) {
      const headingLevel = (_a = content[0].attrs) == null ? void 0 : _a.level;
      return headingLevel === 2 || headingLevel === 3;
    } else {
      return false;
    }
  }
);
const isTextStylesMatchDocumentStylesResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_STYLES_MATCH_DOCUMENT_STYLES,
  (content = [], { styles, nodeService }) => {
    const currentNode = content.filter((node) => {
      return node.type.name === Node_Type.HEADING || node.type.name === Node_Type.PARAGRAPH;
    });
    let stylesMatched = true;
    if (currentNode.length === 1) {
      const ricosCurrentNode = nodeService.nodeToRicosNode(currentNode[0]);
      const currentNodeDocumentsStyles = styles.getDocumentStyle().getDecorations(ricosCurrentNode);
      const textsDecorations = ricosCurrentNode.nodes.filter((node) => {
        return node.type === Node_Type.TEXT;
      }).map((node) => {
        return node.textData.decorations;
      }).flat();
      stylesMatched = textsDecorations.every((decoration) => {
        const currentDocumentStyleDecoration = currentNodeDocumentsStyles.find((documentStyle) => {
          return documentStyle.type === decoration.type;
        });
        if (currentDocumentStyleDecoration) {
          const removeNullReplacer = (key, value) => value === null ? void 0 : value;
          const cleanDecoration = JSON.parse(JSON.stringify(decoration, removeNullReplacer));
          const cleaneDcurrentDocumentStyleDecoration = JSON.parse(
            JSON.stringify(currentDocumentStyleDecoration, removeNullReplacer)
          );
          if (currentDocumentStyleDecoration) {
            if (!isEqual(cleaneDcurrentDocumentStyleDecoration, cleanDecoration)) {
              return false;
            }
          }
        } else {
          return false;
        }
        return true;
      });
    }
    return stylesMatched;
  }
);
const getLineSpacingInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_LINE_SPACING_IN_SELECTION,
  (content, { getEditorQuery }) => {
    const editorQuery = getEditorQuery();
    const activeTextStyles = editorQuery.query.activeTextStyles("lineHeight");
    if (activeTextStyles.length > 0 && activeTextStyles.every((lineHeight, _index, arr) => {
      return lineHeight === arr[0];
    })) {
      return activeTextStyles[0].toString();
    } else {
      return "";
    }
  }
);
const getLineSpacingBeforeSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_LINE_SPACING_BEFORE_SELECTION,
  (content) => {
    var _a;
    if (Array.isArray(content) && content.length > 0) {
      const paddingTop = (_a = content[0].attrs.style) == null ? void 0 : _a.paddingTop;
      if (!paddingTop)
        return void 0;
      return paddingTop;
    } else {
      return void 0;
    }
  }
);
const getLineSpacingAfterSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_LINE_SPACING_AFTER_SELECTION,
  (content) => {
    var _a;
    if (Array.isArray(content) && content.length > 0) {
      const paddingBottom = (_a = content[0].attrs.style) == null ? void 0 : _a.paddingBottom;
      if (!paddingBottom)
        return void 0;
      return paddingBottom;
    } else {
      return void 0;
    }
  }
);
const MANY_FONT_SIZES = "";
const UNKNOWN_FONT_SIZE = "";
const getFontSizeInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_FONT_SIZE_IN_SELECTION,
  (content, { styles, nodeService }, editor) => {
    var _a, _b, _c, _d, _e, _f;
    const getDefaultParagraphValue = () => {
      var _a2, _b2, _c2;
      const decoration = styles.getDecoration(
        {
          paragraphData: {},
          type: Node_Type.PARAGRAPH
        },
        Decoration_Type.FONT_SIZE
      );
      return (_c2 = (_b2 = (_a2 = decoration.fontSizeData) == null ? void 0 : _a2.value) == null ? void 0 : _b2.toString()) != null ? _c2 : UNKNOWN_FONT_SIZE;
    };
    if (!editor) {
      return getDefaultParagraphValue();
    }
    const { state } = editor;
    const { from, $from, to, empty } = state.selection;
    if (empty) {
      const storedFontSize = (_a = state.storedMarks) == null ? void 0 : _a.find(
        (m) => m.type.name === Decoration_Type.FONT_SIZE
      );
      if ((_b = storedFontSize == null ? void 0 : storedFontSize.attrs) == null ? void 0 : _b.value) {
        return storedFontSize.attrs.value.toString();
      }
      const markUnderCursor = $from.marks().find((m) => m.type.name === Decoration_Type.FONT_SIZE);
      if ((_c = markUnderCursor == null ? void 0 : markUnderCursor.attrs) == null ? void 0 : _c.value) {
        return markUnderCursor.attrs.value.toString();
      }
      const parent = state.doc.resolve(from).parent;
      const ricosNode = fromTiptapNode(__spreadProps(__spreadValues({}, parent.toJSON()), { type: (_d = parent == null ? void 0 : parent.type) == null ? void 0 : _d.name }));
      const decoration = styles.getDecoration(ricosNode, Decoration_Type.FONT_SIZE);
      if ((_e = decoration.fontSizeData) == null ? void 0 : _e.value) {
        const fontSizeInDocumentStyle = (_f = decoration.fontSizeData) == null ? void 0 : _f.value;
        return fontSizeInDocumentStyle.toString();
      }
      return UNKNOWN_FONT_SIZE;
    }
    const selectedFontSizes = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
      var _a2, _b2, _c2;
      if (node.type.name === "text") {
        const fontSizeMark = node.marks.find((mark) => mark.type.name === Decoration_Type.FONT_SIZE);
        if (fontSizeMark) {
          selectedFontSizes.push(`${fontSizeMark == null ? void 0 : fontSizeMark.attrs.value}`);
        } else {
          const parent = state.doc.resolve(pos).parent;
          const ricosNode = fromTiptapNode(__spreadProps(__spreadValues({}, parent.toJSON()), { type: (_a2 = parent == null ? void 0 : parent.type) == null ? void 0 : _a2.name }));
          const decoration = styles.getDecoration(
            ricosNode,
            Decoration_Type.FONT_SIZE
          );
          if ((_b2 = decoration.fontSizeData) == null ? void 0 : _b2.value) {
            const fontSizeInDocumentStyle = (_c2 = decoration.fontSizeData) == null ? void 0 : _c2.value;
            selectedFontSizes.push(`${fontSizeInDocumentStyle}`);
          }
        }
      }
    });
    if (selectedFontSizes.length === 0) {
      return getDefaultParagraphValue();
    }
    return selectedFontSizes.every((fontSize) => fontSize === selectedFontSizes[0]) ? selectedFontSizes[0] : MANY_FONT_SIZES;
  }
);
const isTextContainsLinkOrAnchorResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_LINK_OR_ANCHOR,
  (content) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      return node == null ? void 0 : node.marks.some(
        (mark) => mark.type.name === Decoration_Type.LINK || mark.type.name === Decoration_Type.ANCHOR
      );
    }
    return false;
  }
);
const isTextContainsLinkResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_LINK,
  (content) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      return node == null ? void 0 : node.marks.some((mark) => mark.type.name === Decoration_Type.LINK);
    }
    return false;
  }
);
const isTextContainsAnchorResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_TEXT_CONTAINS_ANCHOR,
  (content) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      return node == null ? void 0 : node.marks.some((mark) => mark.type.name === Decoration_Type.ANCHOR);
    }
    return false;
  }
);
const getTextColorInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_TEXT_COLOR_IN_SELECTION,
  (content, { styles, nodeService }, editor) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      const colorMark = node == null ? void 0 : node.marks.find((mark) => mark.type.name === Mark_Type.COLOR_FOREGROUND);
      let textColorInDocumentStyle;
      if (node && styles && nodeService) {
        for (const node2 of content) {
          const decoration = styles.getDecoration(
            nodeService.nodeToRicosNode(node2),
            Decoration_Type.COLOR
          );
          if ((_a = decoration.colorData) == null ? void 0 : _a.foreground) {
            textColorInDocumentStyle = (_b = decoration.colorData) == null ? void 0 : _b.foreground;
            break;
          }
        }
      }
      const storedMark = (_d = (_c = editor == null ? void 0 : editor.state) == null ? void 0 : _c.storedMarks) == null ? void 0 : _d.find((mark) => {
        return mark.type.name === Mark_Type.COLOR_FOREGROUND && mark.attrs.color;
      });
      const markInSelection = (_f = (_e = editor == null ? void 0 : editor.state) == null ? void 0 : _e.selection) == null ? void 0 : _f.$from.marks().find((mark) => {
        return mark.type.name === Mark_Type.COLOR_FOREGROUND && mark.attrs.color;
      });
      let colorInStoredMark;
      let colorInSelection;
      if (storedMark) {
        colorInStoredMark = storedMark.attrs.color;
      }
      if (markInSelection) {
        colorInSelection = markInSelection.attrs.color;
      }
      return (colorMark == null ? void 0 : colorMark.attrs.color) || textColorInDocumentStyle || colorInStoredMark || colorInSelection || ((_g = styles.getTheme().getColors()) == null ? void 0 : _g.textColor);
    }
    return false;
  }
);
const getHighlightColorInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_HIGHLIGHT_COLOR_IN_SELECTION,
  (content, { styles, nodeService }, editor) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      let highlightColorInDocumentStyle;
      if (node && styles && nodeService) {
        for (const node2 of content) {
          const decoration = styles.getDecoration(
            nodeService.nodeToRicosNode(node2),
            Decoration_Type.COLOR
          );
          if ((_a = decoration.colorData) == null ? void 0 : _a.background) {
            highlightColorInDocumentStyle = (_b = decoration.colorData) == null ? void 0 : _b.background;
            break;
          }
        }
      }
      const storedMark = (_d = (_c = editor == null ? void 0 : editor.state) == null ? void 0 : _c.storedMarks) == null ? void 0 : _d.find((mark) => {
        return mark.type.name === Mark_Type.COLOR_BACKGROUND && mark.attrs.color;
      });
      const markInSelection = (_f = (_e = editor == null ? void 0 : editor.state) == null ? void 0 : _e.selection) == null ? void 0 : _f.$from.marks().find((mark) => {
        return mark.type.name === Mark_Type.COLOR_BACKGROUND && mark.attrs.color;
      });
      let colorInStoredMark, colorInSelection;
      if (storedMark) {
        colorInStoredMark = storedMark.attrs.color;
      }
      if (markInSelection) {
        colorInSelection = markInSelection.attrs.color;
      }
      const colorMark = node == null ? void 0 : node.marks.find((mark) => mark.type.name === Mark_Type.COLOR_BACKGROUND);
      return (colorMark == null ? void 0 : colorMark.attrs.color) || highlightColorInDocumentStyle || colorInStoredMark || colorInSelection || ((_g = styles.getTheme().getColors()) == null ? void 0 : _g.bgColor);
    }
    return false;
  }
);
const isNodeContainsLinkOrAnchorResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_NODE_CONTAINS_LINK_OR_ANCHOR,
  (_, __, editor) => {
    var _a, _b, _c;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    if (node) {
      return (_c = node.attrs) == null ? void 0 : _c.link;
    } else {
      return void 0;
    }
  }
);
const isNodeSelected = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_NODE_SELECTED,
  (_, __, editor) => {
    var _a, _b;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    return !!node;
  }
);
const getNodeInSelectionResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_IN_SELECTION,
  (_, __, editor) => {
    var _a, _b;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    return node;
  }
);
const getNodeAlignmentResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_ALIGNMENT,
  (_, __, editor) => {
    var _a, _b, _c, _d;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    if (node) {
      return (_d = (_c = node.attrs) == null ? void 0 : _c.containerData) == null ? void 0 : _d.alignment;
    } else {
      return void 0;
    }
  }
);
const getNodeSizeResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_SIZE,
  (_, __, editor) => {
    var _a, _b, _c, _d, _e;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    if (node) {
      return (_e = (_d = (_c = node.attrs) == null ? void 0 : _c.containerData) == null ? void 0 : _d.width) == null ? void 0 : _e.size;
    } else {
      return void 0;
    }
  }
);
const getNodeTextWrapResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_TEXT_WRAP,
  (_, __, editor) => {
    var _a, _b, _c, _d;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    if (node) {
      return (_d = (_c = node.attrs) == null ? void 0 : _c.containerData) == null ? void 0 : _d.textWrap;
    } else {
      return void 0;
    }
  }
);
const getNodeLinkDataResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_NODE_LINK_DATA,
  (_, __, editor) => {
    var _a, _b, _c;
    const node = (_b = (_a = editor == null ? void 0 : editor.state) == null ? void 0 : _a.selection) == null ? void 0 : _b.node;
    if (node) {
      return (_c = node.attrs) == null ? void 0 : _c.link;
    } else {
      return void 0;
    }
  }
);
const getUrlLinkData = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_URL_LINK_DATA,
  (content) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      if (node == null ? void 0 : node.marks.some((mark) => mark.type.name === Decoration_Type.LINK)) {
        return node.marks[0].attrs.link;
      }
    }
    return false;
  }
);
const getAnchorLinkData = TiptapContentResolver.create(
  RESOLVERS_IDS.GET_ANCHOR_LINK_DATA,
  (content) => {
    if (Array.isArray(content)) {
      const node = content.find((node2) => {
        return node2.type.name === "text";
      });
      if (node == null ? void 0 : node.marks.some((mark) => mark.type.name === Decoration_Type.ANCHOR)) {
        return node.marks[0].attrs.anchor;
      }
    }
    return false;
  }
);
const isUndoStackEmptyResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_UNDO_STACK_EMPTY,
  (_, __, editor) => {
    return !(editor == null ? void 0 : editor.can().undo());
  }
);
const isRedoStackEmptyResolver = TiptapContentResolver.create(
  RESOLVERS_IDS.IS_REDO_STACK_EMPTY,
  (_, __, editor) => {
    return !(editor == null ? void 0 : editor.can().redo());
  }
);

export { alwaysActiveResolver, alwaysVisibleResolver, getAlignmentInSelectionResolver, getAnchorLinkData, getFontSizeInSelectionResolver, getHeadingInSelectionResolver, getHighlightColorInSelectionResolver, getLineSpacingAfterSelectionResolver, getLineSpacingBeforeSelectionResolver, getLineSpacingInSelectionResolver, getNodeAlignmentResolver, getNodeInSelectionResolver, getNodeLinkDataResolver, getNodeSizeResolver, getNodeTextWrapResolver, getTextColorInSelectionResolver, getUrlLinkData, isHeaderTwoOrThreeSelected, isNodeContainsLinkOrAnchorResolver, isNodeSelected, isRedoStackEmptyResolver, isTextAlignCenter, isTextAlignJustify, isTextAlignLeft, isTextAlignRight, isTextContainsAnchorResolver, isTextContainsBoldResolver, isTextContainsCodeblockResolver, isTextContainsItalicResolver, isTextContainsLinkOrAnchorResolver, isTextContainsLinkResolver, isTextContainsOrderedListResolver, isTextContainsQuoteResolver, isTextContainsSpoilerResolver, isTextContainsUnderlineResolver, isTextContainsUnorderedListResolver, isTextInSelection, isTextStylesMatchDocumentStylesResolver, isUndoStackEmptyResolver };
//# sourceMappingURL=tiptapResolvers.js.map
